/**
 * Kitchen Display System (KDS) - Professionelles Design
 * 
 * Basiert auf Best Practices von:
 * - orderbird, ready2order, Toast POS, Lightspeed, Square
 * 
 * Features:
 * - Farbcodierte Station-Header
 * - Gang-Gruppierung (Vorspeise, Hauptgang, etc.)
 * - Modifikationen hervorgehoben
 * - Allergiewarnung prominent
 * - Wartezeit mit Alert
 * - Touch-optimiert für Küchen-Tablets
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChefHat, Clock, Check, RefreshCw, AlertTriangle,
  Undo2, Volume2, VolumeX, Settings, X, Flame, 
  GlassWater, Salad, Pizza, Truck, UtensilsCrossed
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Station-Konfiguration mit Farben und Icons
const STATIONS = {
  grill: { name: 'GRILL', color: 'from-red-600 to-red-700', icon: Flame, emoji: '🔥' },
  bar: { name: 'BAR', color: 'from-blue-600 to-blue-700', icon: GlassWater, emoji: '🍹' },
  salad: { name: 'SALAT', color: 'from-green-600 to-green-700', icon: Salad, emoji: '🥗' },
  pizza: { name: 'PIZZA', color: 'from-orange-600 to-orange-700', icon: Pizza, emoji: '🍕' },
  kitchen: { name: 'KÜCHE', color: 'from-slate-600 to-slate-700', icon: ChefHat, emoji: '👨‍🍳' },
  delivery: { name: 'LIEFERUNG', color: 'from-purple-600 to-purple-700', icon: Truck, emoji: '🚗' }
};

// Status-Konfiguration
const STATUS_CONFIG = {
  new: { 
    label: 'NEU', 
    color: 'bg-red-500', 
    textColor: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500',
    emoji: '⚡'
  },
  rush: { 
    label: 'EILT', 
    color: 'bg-orange-500', 
    textColor: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500',
    emoji: '🔔'
  },
  preparing: { 
    label: 'IN ARBEIT', 
    color: 'bg-amber-500', 
    textColor: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500',
    emoji: '👨‍🍳'
  },
  ready: { 
    label: 'FERTIG', 
    color: 'bg-emerald-500', 
    textColor: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500',
    emoji: '✅'
  },
  served: { 
    label: 'SERVIERT', 
    color: 'bg-slate-500', 
    textColor: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500',
    emoji: '🍽️'
  }
};

export default function KitchenDisplay() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [stats, setStats] = useState(null);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [currentStation, setCurrentStation] = useState('kitchen'); // Alle Stationen

  // Audio für neue Bestellungen
  const playNewOrderSound = useCallback(() => {
    if (soundEnabled) {
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleEdAj2lhUFdne4');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {}
    }
  }, [soundEnabled]);

  // Bestellungen laden
  const loadOrders = useCallback(async () => {
    try {
      const headers = sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
      const demoTenantId = 'e8c127ae-6271-4830-bb04-fed1e1531a0a';
      
      let url;
      if (token) {
        url = `${BACKEND_URL}/api/kds/display/${token}`;
      } else if (sessionToken) {
        url = `${BACKEND_URL}/api/kds/orders`;
      } else {
        url = `${BACKEND_URL}/api/kds/demo/${demoTenantId}`;
      }
      
      const response = await fetch(url, { headers });
      
      if (response.ok) {
        const data = await response.json();
        const newOrders = data.orders || [];
        
        if (newOrders.length > lastOrderCount && lastOrderCount > 0) {
          playNewOrderSound();
        }
        setLastOrderCount(newOrders.length);
        setOrders(newOrders);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, token, lastOrderCount, playNewOrderSound]);

  // Stats laden
  const loadStats = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/kds/stats`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Stats error:', error);
    }
  }, [sessionToken]);

  // Auto-Refresh
  useEffect(() => {
    loadOrders();
    loadStats();
    
    const interval = setInterval(() => {
      loadOrders();
      loadStats();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [loadOrders, loadStats]);

  // Bump Order
  const bumpOrder = async (orderId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/kds/orders/${orderId}/bump`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`${STATUS_CONFIG[result.new_status]?.label || result.new_status}`);
        loadOrders();
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Recall Order
  const recallOrder = async (orderId, e) => {
    e.stopPropagation();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/kds/orders/${orderId}/recall`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        toast.success('Bestellung zurückgeholt');
        loadOrders();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  // Format Wartezeit
  const formatWaitTime = (minutes) => {
    if (minutes < 1) return '< 1 Min';
    if (minutes < 60) return `${Math.round(minutes)} Min`;
    return `${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}m`;
  };

  // Gruppiere Items nach Gang
  const groupItemsByCourse = (items) => {
    const courses = {};
    items?.forEach(item => {
      const course = item.course || item.category || 'Hauptgang';
      if (!courses[course]) courses[course] = [];
      courses[course].push(item);
    });
    return courses;
  };

  // Bestimme Station basierend auf Items
  const getOrderStation = (order) => {
    if (order.source === 'delivery') return 'delivery';
    // Hier könnte Logik basierend auf Kategorien kommen
    return 'kitchen';
  };

  // Statistiken
  const ordersByStatus = {
    new: orders.filter(o => o.status === 'new'),
    preparing: orders.filter(o => o.status === 'preparing'),
    ready: orders.filter(o => o.status === 'ready')
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <RefreshCw className="w-12 h-12 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white select-none" data-testid="kds-display">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Kitchen Display</h1>
              <p className="text-xs text-slate-400">Cash-X KDS</p>
            </div>
          </div>
          
          {stats?.today && (
            <div className="hidden md:flex items-center gap-4 ml-6">
              <div className="text-center">
                <div className="text-lg font-bold text-amber-400">{stats.today.avg_prep_time_minutes}</div>
                <div className="text-xs text-slate-400">Ø Min</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-emerald-400">{stats.today.completed_orders}</div>
                <div className="text-xs text-slate-400">Fertig</div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Station Selector */}
          <select 
            className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm"
            value={currentStation}
            onChange={(e) => setCurrentStation(e.target.value)}
          >
            <option value="kitchen">Alle Stationen</option>
            {Object.entries(STATIONS).map(([key, station]) => (
              <option key={key} value={key}>{station.emoji} {station.name}</option>
            ))}
          </select>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="text-slate-400 hover:text-white"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadOrders}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          {sessionToken && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/app/einstellungen')}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>
      </header>

      {/* Status Summary Bar */}
      <div className="grid grid-cols-3 gap-1 p-2 bg-slate-900/80">
        <button 
          className={`py-3 rounded-lg transition-all ${
            ordersByStatus.new.length > 0 ? 'bg-red-500/30 hover:bg-red-500/40' : 'bg-slate-800/50'
          }`}
          onClick={() => {/* Filter by status */}}
        >
          <span className={`text-3xl font-bold ${ordersByStatus.new.length > 0 ? 'text-red-400 animate-pulse' : 'text-slate-500'}`}>
            {ordersByStatus.new.length}
          </span>
          <span className="text-xs text-slate-300 ml-2">NEU</span>
        </button>
        <button 
          className={`py-3 rounded-lg transition-all ${
            ordersByStatus.preparing.length > 0 ? 'bg-amber-500/30 hover:bg-amber-500/40' : 'bg-slate-800/50'
          }`}
        >
          <span className={`text-3xl font-bold ${ordersByStatus.preparing.length > 0 ? 'text-amber-400' : 'text-slate-500'}`}>
            {ordersByStatus.preparing.length}
          </span>
          <span className="text-xs text-slate-300 ml-2">IN ARBEIT</span>
        </button>
        <button 
          className={`py-3 rounded-lg transition-all ${
            ordersByStatus.ready.length > 0 ? 'bg-emerald-500/30 hover:bg-emerald-500/40' : 'bg-slate-800/50'
          }`}
        >
          <span className={`text-3xl font-bold ${ordersByStatus.ready.length > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
            {ordersByStatus.ready.length}
          </span>
          <span className="text-xs text-slate-300 ml-2">FERTIG</span>
        </button>
      </div>

      {/* Orders Grid */}
      <main 
        className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 overflow-auto" 
        style={{ height: 'calc(100vh - 140px)' }}
      >
        {orders.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-20">
            <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mb-6">
              <ChefHat className="w-12 h-12 text-slate-600" />
            </div>
            <p className="text-2xl text-slate-500 font-medium">Keine offenen Bestellungen</p>
            <p className="text-slate-600 mt-2">Neue Bestellungen erscheinen automatisch</p>
          </div>
        ) : (
          orders.map((order) => {
            const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.new;
            const station = STATIONS[getOrderStation(order)] || STATIONS.kitchen;
            const groupedItems = groupItemsByCourse(order.items);
            
            return (
              <div 
                key={order.order_id}
                onClick={() => bumpOrder(order.order_id)}
                data-testid={`kds-order-${order.order_id}`}
                className={`
                  cursor-pointer transition-all duration-200 
                  hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]
                  rounded-xl overflow-hidden border-2
                  ${status.borderColor} ${status.bgColor}
                  ${order.is_alert ? 'animate-pulse ring-2 ring-red-500' : ''}
                `}
              >
                {/* Station Header */}
                <div className={`bg-gradient-to-r ${station.color} px-4 py-2 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{station.emoji}</span>
                    <span className="font-bold text-sm tracking-wider">{station.name}</span>
                  </div>
                  {order.status !== 'new' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => recallOrder(order.order_id, e)}
                      className="text-white/70 hover:text-white hover:bg-white/20 h-7 w-7 p-0"
                    >
                      <Undo2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Order Info */}
                <div className="px-4 py-3 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">#{order.order_id?.slice(-4) || '0000'}</span>
                    <div className="bg-white text-black px-3 py-1 rounded font-bold text-sm">
                      {order.table_name || 'Theke'}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <Badge className={`${status.color} text-white text-xs`}>
                        {status.emoji} {status.label}
                      </Badge>
                      {order.source === 'qr_code' && (
                        <Badge variant="outline" className="border-blue-500 text-blue-400 text-xs">QR</Badge>
                      )}
                      {order.source === 'delivery' && (
                        <Badge variant="outline" className="border-purple-500 text-purple-400 text-xs">🚗</Badge>
                      )}
                    </div>
                    <div className={`flex items-center gap-1 text-sm ${order.is_alert ? 'text-red-400 font-bold' : 'text-slate-400'}`}>
                      <Clock className="w-3.5 h-3.5" />
                      {formatWaitTime(order.wait_minutes)}
                      {order.is_alert && <AlertTriangle className="w-3.5 h-3.5 animate-bounce" />}
                    </div>
                  </div>
                </div>

                {/* Items grouped by Course */}
                <div className="px-4 py-3 space-y-3 max-h-64 overflow-auto">
                  {Object.entries(groupedItems).map(([course, items]) => (
                    <div key={course}>
                      {/* Course Header */}
                      {Object.keys(groupedItems).length > 1 && (
                        <div className="bg-white/10 text-center py-1 rounded text-xs font-bold tracking-wider mb-2">
                          ▼ {course.toUpperCase()} ▼
                        </div>
                      )}
                      
                      {/* Items */}
                      {items.map((item, idx) => (
                        <div key={idx} className="mb-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold text-amber-400">
                              {item.quantity || item.qty || 1}x
                            </span>
                            <span className="font-bold text-white uppercase">
                              {item.product_name || item.name}
                            </span>
                          </div>
                          
                          {/* Modifikationen (invertiert) */}
                          {item.modifiers?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1 ml-8">
                              {item.modifiers.map((mod, midx) => (
                                <span 
                                  key={midx}
                                  className="bg-white text-black px-2 py-0.5 rounded text-xs font-bold"
                                >
                                  {typeof mod === 'object' ? mod.name : mod}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Entfernte Zutaten */}
                          {item.removed_ingredients?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1 ml-8">
                              {item.removed_ingredients.map((ing, iidx) => (
                                <span 
                                  key={iidx}
                                  className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold"
                                >
                                  OHNE {ing}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Notizen */}
                          {item.notes && (
                            <p className="text-amber-400 text-sm mt-1 ml-8">
                              → {item.notes}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Allergien */}
                {order.allergy_info && (
                  <div className="mx-4 mb-3 p-2 border-2 border-red-500 bg-red-500/20 rounded-lg text-center">
                    <span className="text-lg">⚠️</span>
                    <span className="font-bold text-red-400 ml-2 uppercase text-sm">
                      ALLERGIE: {order.allergy_info}
                    </span>
                  </div>
                )}

                {/* Kundennotiz */}
                {(order.notes || order.customer_note) && (
                  <div className="mx-4 mb-3 p-2 bg-amber-500/20 border-l-4 border-amber-500 rounded">
                    <div className="text-xs font-bold text-amber-400 mb-1">📝 HINWEIS:</div>
                    <p className="text-sm text-amber-200">{order.notes || order.customer_note}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="px-4 py-2 bg-black/30 flex items-center justify-between text-xs text-slate-400">
                  <span>Positionen: {order.items?.reduce((sum, i) => sum + (i.quantity || i.qty || 1), 0) || 0}</span>
                  <span>Tippen zum Weiterschieben →</span>
                </div>
              </div>
            );
          })
        )}
      </main>
    </div>
  );
}
